import React from "react"
import * as taskAPI from "../../tasks/api"
import { useDateFormat } from "../../users/hooks/use-date-format"
import { TreeGridTranslations, useTranslations } from "../hooks/use-translations"
import { useTreeGridFilters } from "../hooks/use-treegrid-filters"
import { useLanguage } from "../../i18n/use-language"
import { useHistory } from "react-router-dom"
import { TaskTreeGridRow } from "../types"
import {
  getColorFromColorHtml,
  getGanttUnitFromZoom,
  getLagUnitFromText,
  getStateFromStateIcon,
  makeEnumString,
  mapTranslationsToColor,
} from "../utils"
import { useOptions } from "../../options/hooks/use-options"
import { useTaskArchiveFeedback } from "../hooks/use-task-archive-feedback"
import { useAddTaskButton } from "../hooks/use-show-add-task-button"
import { getLatestCreatedTasks, createTreegrid, destroyTreegrid, syncDataFromServerToGrid } from "../utils/tree-grid"
import { useUserTimeZone } from "../../hooks/use-user-time-zone"
import { DateTimeService } from "../../services/date-time-service"
import { useUpdateTaskStatus } from "../../tasks/hooks/use-update-task-status"
import { useUrlWithContext } from "../../hooks/use-url-with-context"
import { makeRootNode } from "./root-node"
import { makeZooms } from "../base/layout/make-zooms"
import { makeLayout } from "./layout"
import { getTaskTreeGridGanttDates, syncAddedAndUpdatedTasks } from "./utils"
import { makeTaskTreegridRows } from "./rows"
import { makeTreeGridTask } from "./tree-grid-task"
import { TimeZoneType } from "../../constants/timezones"
import { useAddedRows } from "../hooks/use-added-rows"
import { getMembershipOptions } from "../../options/utils"
import { useTaskMutations } from "../../tasks/hooks/use-task-mutations"
import { TaskViewModel } from "../../tasks/api/task"
import { ProjectViewModel } from "../../projects/api/project"
import { makeShowContextMenu } from "./show-context-menu"
import { SystemStatus } from "../../lib/system-status"
import { OnboardingGuideModal } from "../utils/onBoardingshow"
import { useTranslation } from "react-i18next"
import { useSnackbar } from "notistack"
import { MaintainerTypeEnum } from "../../types/common"
import { getGanttVisibleRange } from "../utils/gantt-utils"
import { useTimeout } from "../../hooks/use-timeout"
import { makeOnFilterFinish } from "../base/event-handlers/on-filter-finish"
import { makeOnSearchChange } from "../base/event-handlers/on-search-change"
import { makeOnAfterSectionResize } from "../base/event-handlers/on-after-section-resize"
import { CalendarSettingsViewModel } from "../../calendar-settings/api/calendar-settings"
import { createHolidayChecker, createWeekendChecker } from "../../calendar-settings/utils/date"
import { useFileDownload } from "../../storage/hooks/use-file-download"

const DEFAULT_CALENDAR_SETTINGS: CalendarSettingsViewModel = {
  id: "default",
  type: "project",
  firstDayOfWeek: 0,
  weekendDays: [],
  holidayDetails: [],
  fixedHolidays: [],
  recurringHolidays: [],
  dateFnsDateFormat: "MM/dd/yyyy",
  timeZone: "UTC",
  dateFormatSpecs: {
    dateOrder: "DD.MM.YYYY",
    dateSeparator: ".",
  },
}

export const useTaskTreeGrid = ({
  project,
  task,
  tasks,
  calendarSettings = DEFAULT_CALENDAR_SETTINGS,
  onAddTask,
  showActualBar = false,
  showBaselineBar = false,
  showGantt = false,
}: TaskTreeGridProps) => {
  const root = project || task
  const gridId = project ? `__project_${showGantt ? "gantt" : "task"}_treegrid__` : "__task_treegrid__"
  const id = gridId
  const canCreateRootTasks = Boolean(project?.canCreateProjectTasks || task?.canCreate)
  const isUserRoot = project ? project.isUserProject : task.isUserTask
  const { enableTimeComponent } = root
  const history = useHistory()
  const locale = useLanguage()
  const translations = useTranslations()
  const projectTranslations = useTranslation("project").t

  const { t: tTreegrid } = useTranslation("treegrid")
  const { t: commonTranslations } = useTranslation("common")

  const timeZone = useUserTimeZone()
  const { dateFormat } = useDateFormat()
  const { checkIsHoliday, checkIsWeekend } = React.useMemo(() => {
    const checkIsHoliday = createHolidayChecker(calendarSettings)
    const checkIsWeekend = createWeekendChecker(calendarSettings)
    return {
      checkIsHoliday,
      checkIsWeekend,
    }
  }, [calendarSettings])

  const { showFilters, toggleFilters } = useTreeGridFilters({ gridId })
  const { setAddedRows } = useAddedRows({ gridId })
  const { showAddTaskButton, disableAddTaskButton, setDisableAddTaskButton } = useAddTaskButton({
    canCreateRootTasks,
    translations,
  })
  const [navigateToTaskId, setNavigateToTaskId] = React.useState<null | string>(null)
  const [showGuide, setShowGuide] = React.useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { onArchiveSuccess } = useTaskArchiveFeedback({ gridId, dateFormat, timeZone })
  const taskActions = useTaskMutations()
  const { createPathWithContext } = useUrlWithContext()
  const orgId = root.maintainerType === MaintainerTypeEnum.ORG ? root.maintainerId : undefined
  const { customerOptions, membershipOptions, supplierOptions, workspaceOptions } = useOptions(orgId)
  const options = isUserRoot ? [] : [...customerOptions, ...membershipOptions, ...supplierOptions, ...workspaceOptions]
  const zoomRef = React.useRef<string | null>(null)
  const scrollToDateRef = React.useRef<Date | null>(null)
  const { set: scheduleZoom } = useTimeout()

  const rootNode = makeRootNode({ root, dateFormat, timeZone })
  const { minStartDate, maxEndDate, defaultZoomStartDate, defaultZoomEndDate, zoomFitStartDate, zoomFitEndDate } =
    getTaskTreeGridGanttDates(rootNode, tasks)
  const defaultGanttZoomRange = getGanttVisibleRange({
    startDate: defaultZoomStartDate,
    endDate: defaultZoomEndDate,
  })

  const { zoomList, defaultZoom } = makeZooms({
    translations,
    enableTimeComponent,
    leftEdgeRefDate: minStartDate,
    rightEdgeRefDate: maxEndDate,
  })
  const rows = makeTaskTreegridRows({ rootNode, tasks, dateFormat, timeZone, translations })

  const onUpdateTaskStatusSuccess = React.useCallback(
    (updatedTasks: TaskViewModel[]) => {
      const grid = window.Grids[id]
      updateRowsInGrid({ grid, dateFormat, timeZone, updatedTasks, translations, tasks })
    },
    [id, dateFormat, timeZone, translations, tasks]
  )
  const { updateTaskStatus } = useUpdateTaskStatus({ onSuccess: onUpdateTaskStatusSuccess })
  const { openFileInBrowser } = useFileDownload()

  const applyWeekNumberPrefixLabel = (t: (key: string) => string) => {
    const label = t("week.short") + " "
    document.documentElement.style.setProperty("--week-label", `"${label}"`)
  }
  // Set the Week number prefix in calendar to the correct translation on initial load
  React.useEffect(() => {
    applyWeekNumberPrefixLabel(commonTranslations)
  }, [commonTranslations, gridId])

  React.useEffect(() => {
    // Check if the guide was already shown
    const guideShown = localStorage.getItem("taskTreeGridGuideShown")
    if (guideShown !== null && !guideShown && tasks.length === 0) {
      setShowGuide(true)
    }
  }, [tasks.length])

  const handleGuideComplete = () => {
    localStorage.setItem("taskTreeGridGuideShown", "true")
    setShowGuide(false)
  }
  // Adding keydown listener to handle ctrl/cmd + c and ctrl/cmd + v for copy paste functionality,
  // and stopping the event propagation so that it doesn't trigger any other copy paste handlers
  // in the app when the treegrid is focused.
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "v")) {
        const grid = window.Grids[id]
        if (grid) {
          e.stopImmediatePropagation()
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown, true) // true = capture phase
    return () => document.removeEventListener("keydown", handleKeyDown, true)
  }, [id])

  // TODO memoize
  const layout = makeLayout({
    id,
    root: rootNode,
    showActualBar,
    showBaselineBar,
    showGantt,
    showGanttToolbarActions: showGantt,
    showAddTaskButton,
    showFilters,
    translations,
    options,
    locale,
    defaultZoom,
    zoomList,
    calendarSettings,
  })

  const dateTimeService = React.useMemo(
    () => new DateTimeService({ dateFormat, timeZone, enableTimeComponent }),
    [dateFormat, timeZone, enableTimeComponent]
  )

  if (window.Grids) {
    // @ts-ignore
    window.Grids.OnSearchChange = makeOnSearchChange({ translations })
    window.Grids.OnFilterFinish = makeOnFilterFinish({ translations, gridId: "task-tree-grid" })
    window.Grids.OnAfterSectionResize = makeOnAfterSectionResize({ ganttZoomRange: defaultGanttZoomRange })

    window.Grids.OnRenderStart = function (grid) {
      const onAddTaskFromContextMenu = async (data: Omit<taskAPI.NewTaskData, "orgId" | "projectId">) => {
        const responseData = await onAddTask({ ...data, taskCount: 1 })
        if (responseData) {
          const addedTasks = responseData.created
          const updatedTasks = responseData.updated.tasks
          syncAddedAndUpdatedTasks({ grid, addedTasks, updatedTasks, dateFormat, timeZone, setAddedRows, translations })
        }
        return responseData
      }

      const onCopyTaskFromContextMenu = async (taskId: string, options: Omit<taskAPI.CopyTaskData, "taskId">) => {
        const copyResult = await taskActions.copyTask(taskId, options)
        if (!copyResult || !grid) return
        const addedTasks = getLatestCreatedTasks(copyResult.tasks)
        const updatedTasks = tasks.filter((task) => {
          return addedTasks.findIndex((addedTask) => addedTask.id === task.id) === -1
        })
        syncAddedAndUpdatedTasks({
          grid,
          addedTasks,
          updatedTasks,
          dateFormat,
          timeZone,
          setAddedRows,
          translations,
        })
        return { added: addedTasks, updated: updatedTasks }
      }

      const { showContextMenu } = makeShowContextMenu({
        rootNode,
        dateFormat,
        timeZone,
        onAddTask: onAddTaskFromContextMenu,
        onCopyTask: onCopyTaskFromContextMenu,
        translations,
      })

      // @ts-ignore
      grid["showContextMenu"] = showContextMenu
    }

    window.Grids.OnEndDrag = function (
      grid: any,
      origRow: TaskTreeGridRow & TRow,
      __: any,
      destRow: TaskTreeGridRow & TRow,
      type: number
    ) {
      const projectId = rootNode.projectId
      const taskId = origRow.id
      const fromOrder = origRow?.order
      const toOrder = destRow?.order
      const origRowParentTaskId = origRow.parentNode?.id || ""
      const destRowParentTaskId = destRow.parentNode?.id || ""
      const parentTask = grid.GetRowById(destRowParentTaskId)
      const isAllowedToDrag = parentTask ? parentTask?.canCreateTasks : canCreateRootTasks
      if (!isAllowedToDrag) return 0 // return 0 to cancel the drag

      // type === 1 means Task is being moved above destRow
      if (type === 1) moveTaskAbove()
      // type === 2 means Task is being moved as a child of destRow
      if (type === 2) moveTaskAsChild()
      // type === 3 means Task is being moved below destRow
      if (type === 3) moveTaskBelow()

      function moveTaskAsChild() {
        taskActions.updateOrder(taskId, { projectId, parentTaskId: destRow.id }).then((updateResult) => {
          if (!updateResult) return
          const updatedTasks = updateResult.tasks
          if (updatedTasks.length) updateRowsInGrid({ translations, grid, dateFormat, timeZone, updatedTasks, tasks })
        })
        return
      }

      function moveTaskAbove() {
        let order
        if (origRowParentTaskId === destRowParentTaskId) {
          order = fromOrder < toOrder ? toOrder - 1 : toOrder
        } else {
          order = toOrder
        }
        taskActions
          .updateOrder(taskId, { projectId, order, parentTaskId: destRowParentTaskId })
          .then((updateResult) => {
            if (!updateResult) return
            const updatedTasks = updateResult.tasks
            if (updatedTasks.length) updateRowsInGrid({ translations, grid, dateFormat, timeZone, updatedTasks, tasks })
          })
        return
      }

      function moveTaskBelow() {
        let order
        if (origRowParentTaskId === destRowParentTaskId) {
          order = fromOrder < toOrder ? toOrder : toOrder + 1
        } else {
          order = toOrder + 1
        }
        taskActions
          .updateOrder(taskId, { projectId, order, parentTaskId: destRowParentTaskId })
          .then((updateResult) => {
            if (!updateResult) return
            const updatedTasks = updateResult.tasks
            if (updatedTasks.length) updateRowsInGrid({ translations, grid, dateFormat, timeZone, updatedTasks, tasks })
          })
        return
      }
    }

    // @ts-ignore
    window.Grids.OnAddTask = function (grid, row, col, event: PointerEvent) {
      if (!grid) return
      const eventTargetId = (event.target as HTMLElement).getAttribute("id")
      const isAddTaskButtonClick = eventTargetId === "addTaskButtonClickArea"

      if (isAddTaskButtonClick) {
        const isAddingTaskToEmptyProject = tasks.length === 0

        const taskCountInputElement = document.querySelector('input[name="taskToAddCount"]') as HTMLInputElement
        const taskCount = taskCountInputElement ? Number(taskCountInputElement.value) : 1
        const title = "Untitled task"
        const parentTaskId = row.id === "Toolbar" ? undefined : row.parentNode?.id || undefined
        const order = row.id === "Toolbar" ? undefined : row.order + 1

        if (disableAddTaskButton) return
        setDisableAddTaskButton(true)

        onAddTask({ title, parentTaskId, order, taskCount }).then((responseData) => {
          setDisableAddTaskButton(false)
          if (responseData && grid) {
            const addedTasks = responseData.created
            const updatedTasks = responseData.updated.tasks
            // Show the guide if it's not shown before and there is no task in the grid
            const guideShown = localStorage.getItem("taskTreeGridGuideShown") === "true"
            if (!guideShown && tasks.length === 0) {
              setShowGuide(true)
            }
            if (isAddingTaskToEmptyProject && showGantt) {
              scheduleZoom(() => {
                // Adding task for the first time messes up the zoom in the gantt chart for some reason,
                // So resetting the zoom to the project's boundary dates to fix the issue.
                const ganttZoomRange = getGanttVisibleRange({
                  startDate: defaultZoomStartDate,
                  endDate: defaultZoomEndDate,
                })
                window.Grids[id]?.ZoomTo(ganttZoomRange.start, ganttZoomRange.end)
              }, 100)
            }
            syncAddedAndUpdatedTasks({
              grid,
              addedTasks,
              updatedTasks,
              dateFormat,
              timeZone,
              setAddedRows,
              translations,
            })
          }
        })
      }
    }

    // @ts-ignore
    window.Grids.OnKeyDownAddTaskCount = function (event: KeyboardEvent<HTMLInputElement>) {
      event.stopImmediatePropagation()
      if (event.key === "Enter") {
        const taskCountInputElement = event.target
        const taskCount = taskCountInputElement ? Number(taskCountInputElement.value) : 1
        const title = "Untitled task"
        const grid = window.Grids[id]

        onAddTask({ title, taskCount }).then((responseData) => {
          if (responseData && grid) {
            const addedTasks = responseData.created
            const updatedTasks = responseData.updated.tasks
            syncAddedAndUpdatedTasks({
              grid,
              addedTasks,
              updatedTasks,
              dateFormat,
              timeZone,
              setAddedRows,
              translations,
            })
          }
        })
      }
    }

    window.Grids.OnEndDragGantt = function (grid, row: TRow & TaskTreeGridRow, __, draggedObjectName, start, end) {
      if (
        row.GanttCanEdit === 0 &&
        draggedObjectName === "Main" // The plan bar has gantt object name Main, so its making sure the dragged object is really a plan bar
      ) {
        const startDate: Date = new Date(start)
        const endDate: Date = new Date(end)
        let plannedEndDate: string = dateTimeService.addTimezoneOffset(endDate, "UTC").toISOString()
        let plannedStartDate: string = dateTimeService.addTimezoneOffset(startDate, "UTC").toISOString()
        // If the time component is disabled and the planned end date given by the treegrid has a timestamp of 00:00:00,
        // then we need to subtract 1 millisecond from the planned end date to get the correct end of the day.
        // For example: when the user drags the plan bar to the end of the 18th day the treegrid gives the planned end date as 19th day 00:00:00.
        // But the user wants to schedule the task at the end of the 18th day, so we need to subtract 1 millisecond from the planned end date.
        if (!enableTimeComponent && dateTimeService.isMidnight(plannedEndDate)) {
          plannedEndDate = new Date(new Date(plannedEndDate).getTime() - 1).toISOString()
        }
        taskActions.updatePlan(row.id, { plannedEndDate, plannedStartDate }).then((updateResult) => {
          if (!updateResult) return
          const updatedTasks = updateResult.tasks
          if (!updatedTasks || !updatedTasks.length) return
          updateRowsInGrid({ translations, grid, dateFormat, timeZone, updatedTasks, tasks })
        })
      }
    }

    window.Grids.OnEndDragGanttDependency = function (_, deps) {
      const [newDependency] = deps
      const [ganttAncestorTaskId, toId, type = "fs", lagTime = 0] = newDependency
      const currentZoom = zoomRef.current as string
      const lagUnit = getGanttUnitFromZoom(zoomList, currentZoom)
      const dependencyData = {
        ganttAncestorTaskId,
        toId,
        type,
        lagTime,
        lagUnit,
      }
      taskActions.addDependency(toId, dependencyData).then((updateResult) => {
        if (!updateResult) return
        const updatedTasks = updateResult.tasks
        if (!updatedTasks || !updatedTasks.length) return
        updateRowsInGrid({ translations, grid: window.Grids[id], dateFormat, timeZone, updatedTasks, tasks })
      })
      return false
    }

    window.Grids.OnGanttMenuClick = function (grid, row, col, menuName, menuItem) {
      if (menuName === translations.deleteDependency) {
        const dependency = (menuItem as any).Param?.[0]?.[0]
        const [fromId, toId, type] = dependency
        const dependencyData = { ganttAncestors: [{ taskId: fromId, type }] }
        taskActions.removeDependency(toId, dependencyData).then((updatedTasks) => {
          if (!updatedTasks || !updatedTasks.length) return
          updateRowsInGrid({ translations, grid: window.Grids[id], dateFormat, timeZone, updatedTasks, tasks })
        })
        return
      }
      if (menuName === translations.deleteMainBar) {
        const updateData = { plannedStartDate: "", plannedEndDate: "" }
        taskActions.updatePlan(row.id, updateData).then((updateResult) => {
          if (!updateResult) return
          const updatedTasks = updateResult.tasks
          if (!updatedTasks || !updatedTasks.length) return
          updateRowsInGrid({ translations, grid: window.Grids[id], dateFormat, timeZone, updatedTasks, tasks })
        })
        return
      }
    }

    // Disable editing of weekends based on the user's weekend settings
    window.Grids.OnCanEditDate = function (grid, row, col, date) {
      const isHoliday = checkIsHoliday(date)
      if (isHoliday) return false

      const isWeekend = checkIsWeekend(date)
      if (isWeekend) return false

      return true
    }

    window.Grids.OnValueChanged = function (grid, row: TRow & TaskTreeGridRow, column: string, newValue, oldValue) {
      // NOTE: Returning early from this function will cause unexpected behavior in the treegrid
      const isColumnEditAllowed = (row as any)[`${column}CanEdit`] === 1
      const hasValueChanged = newValue !== oldValue
      const shouldDispatchUpdateRequest = isColumnEditAllowed && hasValueChanged

      if (shouldDispatchUpdateRequest) {
        let value = adaptValue({ column, newValue, oldValue })

        if (isDateColumn(column) && value) {
          const date = new Date(value)
          const adjustedDate = dateTimeService.addTimezoneOffset(date, "UTC")
          value = adjustedDate.toISOString()
        }
        if (column === "plannedStartDate" || column === "plannedEndDate") {
          const startRaw = column === "plannedStartDate" ? value : row.plannedStartDate
          const endRaw = column === "plannedEndDate" ? value : row.plannedEndDateOrig ?? row.plannedEndDate
          if (startRaw && endRaw) {
            const start = new Date(startRaw)
            const end = new Date(endRaw)
            // If the start date is after the end date, we should set the end date to be the same as the start date
            if (end < start) {
              value = start.toISOString()
            }
            // If the start or end date is invalid, we should revert back the update
            else if (start && end && !DateTimeService.validateProjectDuration(start, end)) {
              enqueueSnackbar(tTreegrid("taskDurationError"), { variant: "error" })
              value = oldValue !== "" ? new Date(oldValue).toISOString() : ""
            }
          }
        }
        if (column === "ganttBarColor") {
          row.GanttGanttClass = value === "Default" ? undefined : value // changes the color of the gantt bar immediately
          value = mapTranslationsToColor(translations)[value]
        }

        if (column === "status") {
          const [mainStatusId, subStatusId] = newValue.toString().split("_")
          const status = SystemStatus.statusByIndex[parseInt(mainStatusId)]
          updateTaskStatus(row.id, { status, subStatusId: subStatusId ?? null })
          return newValue
        }

        if (column === "actualStartDate" || column === "actualEndDate") {
          const startRaw = column === "actualStartDate" ? value : row.actualStartDate
          const endRaw = column === "actualEndDate" ? value : row.actualEndDate
          let sendReq = true
          // actual start and end dates cannot be in the future
          if (startRaw || endRaw) {
            // The column being edited comes from TreeGrid in local timezone, needs local conversion
            // The other column comes from server already as UTC ISO, use as-is
            const startISO = startRaw
              ? column === "actualStartDate"
                ? DateTimeService.toLocalISOStringFormat(new Date(startRaw))
                : new Date(startRaw).toISOString()
              : ""
            const endISO = endRaw
              ? column === "actualEndDate"
                ? DateTimeService.toLocalISOStringFormat(new Date(endRaw))
                : new Date(endRaw).toISOString()
              : ""
            const nowISO = enableTimeComponent
              ? DateTimeService.toLocalISOStringFormat(new Date())
              : new Date().toISOString()

            // actual start and end dates cannot be in the future
            if (column === "actualStartDate" && startISO && startISO > nowISO) {
              enqueueSnackbar(projectTranslations("projectDetails.actualStartDateCannotBeInFuture"), {
                variant: "error",
              })
              value = oldValue === "" ? "" : new Date(oldValue).toISOString()
              sendReq = false
            } else if (column === "actualEndDate" && endISO && endISO > nowISO) {
              enqueueSnackbar(projectTranslations("projectDetails.actualEndDateCannotBeInFuture"), { variant: "error" })
              value = oldValue === "" ? "" : new Date(oldValue).toISOString()
              sendReq = false
            }
            // actual start date cannot be after actual end date and vice versa
            else if (startISO && endISO) {
              const startCompare = enableTimeComponent ? startISO : startISO.substring(0, 10)
              const endCompare = enableTimeComponent ? endISO : endISO.substring(0, 10)

              if (column === "actualEndDate" && endCompare < startCompare) {
                enqueueSnackbar(projectTranslations("projectDetails.actualEndCannotBeBeforeStartError"), {
                  variant: "error",
                })
                value = oldValue === "" ? "" : new Date(oldValue).toISOString()
                sendReq = false
              }
              if (column === "actualStartDate" && startCompare > endCompare) {
                enqueueSnackbar(projectTranslations("projectDetails.actualStartCannotBeAfterEndError"), {
                  variant: "error",
                })
                value = oldValue === "" ? "" : new Date(oldValue).toISOString()
                sendReq = false
              }
            }
          }
          // If the value is valid and has passed all the validations,
          // then we can send the update request to the server. If not,
          // we should revert back the value in the grid and not send the request.
          if (sendReq) {
            updateTaskStatus(row.id, { [column]: value })
            return
          }
          return value ? new Date(value) : ""
        }

        // Special handling for multi-select columns to change the value structure
        if (
          column === "managers" ||
          column === "customers" ||
          column === "participants" ||
          column === "suppliers" ||
          column === "workspaces"
        ) {
          const oldIds: string[] = oldValue.split(";").filter(Boolean)
          const newIds: string[] = newValue.split(";").filter(Boolean)

          const add = newIds
            .filter((id) => !oldIds.includes(id))
            .map((id) => {
              const option = options.find((opt) => opt.id === id)
              return { id, name: option?.name ?? "" }
            })

          const remove = oldIds
            .filter((id) => !newIds.includes(id))
            .map((id) => {
              const option = options.find((opt) => opt.id === id)
              return { id, name: option?.name ?? "" }
            })

          value = { add, remove }
        }

        taskActions.initTaskUpdate(row.id, { field: column as any, value }).then((responseData) => {
          if (!responseData || !grid) return
          const updatedTasks = "tasks" in responseData ? responseData.tasks : [responseData]
          updateRowsInGrid({ translations, grid, dateFormat, timeZone, updatedTasks, tasks })
        })
      }
      return newValue
    }

    window.Grids.OnGanttChange = function (
      grid,
      row,
      col,
      item,
      value,
      value2,
      old = null,
      old2 = null,
      action = null
    ) {
      // Treegrid calls this function twice when the dependency lag is updated, once with the updated value and once with empty value.
      // We should only handle when the value is not empty.
      const shouldUpdateDependencyLag = item === "Dependency" && action === "Edit" && value
      if (shouldUpdateDependencyLag) {
        const [ganttAncestorTaskId, taskId, type, lag = ""] = value2
        const regex = /^(-?[\d\s]+)([a-zA-Z]+)?$/
        const [match, time, unit] = lag.match(regex)
        if (!ganttAncestorTaskId || !taskId || !type || !match) return
        const lagTime = Number(time.trim())
        const currentZoom = zoomRef.current as string
        const fallbackUnit = getGanttUnitFromZoom(zoomList, currentZoom)
        const lagUnit = unit ? getLagUnitFromText(unit) : fallbackUnit
        const dependencyData = { ganttAncestorTaskId, type, lagTime, lagUnit }
        taskActions.updateDependency(taskId, dependencyData).then((updateResult) => {
          if (!updateResult) return
          const updatedTasks = updateResult.tasks
          if (!updatedTasks || !updatedTasks.length) return
          updateRowsInGrid({ translations, grid: window.Grids[id], dateFormat, timeZone, updatedTasks, tasks })
        })
      }
    }

    window.Grids.OnRowDelete = function (grid, row, type) {
      handleRowDelete()
      async function handleRowDelete() {
        if (type !== 2) {
          const archiveResult = await taskActions.archiveTask({ taskId: row.id })
          if (!archiveResult) return
          const changedTasks = archiveResult.tasks
          if (changedTasks.length && grid) {
            onArchiveSuccess(row.id, translations.taskArchiveSuccessMessage)
            const archivedTasks = changedTasks.filter((task) => task.archived)
            const archivedTasksSortedByAncestorLength = archivedTasks.sort((a, b) => {
              return b.ancestors.length - a.ancestors.length
            })

            archivedTasksSortedByAncestorLength.forEach((task) => {
              const gridRow = grid.GetRowById(task.id)
              if (gridRow) {
                grid.HideRow(gridRow)
                grid.RemoveRow(gridRow)
              }
            })
            const updatedTasks = changedTasks.filter((task) => !task.archived)
            updateRowsInGrid({ translations, grid, dateFormat, timeZone, updatedTasks, tasks })
          }
        }
      }
    }

    window.Grids.OnGanttTip = function (_g, _r, _c, _tip, event, name) {
      if (name === "Dependency") {
        const { DependencyFrom, DependencyType, DependencyTo } = event
        const fromRowNumber = (_g?.Rows as any)[DependencyFrom]?.[1]
        const toRowNumber = (_g?.Rows as any)[DependencyTo]?.[1]
        return `${fromRowNumber} => ${toRowNumber} (${DependencyType})`
      } else {
        return _tip
      }
    }

    window.Grids.OnRenderFinish = function (grid) {
      if (grid) {
        grid.ZoomTo(defaultGanttZoomRange.start, defaultGanttZoomRange.end)
      }
    }

    window.Grids.OnZoom = function (_, zoom) {
      zoomRef.current = zoom
    }
    // @ts-ignore
    window.Grids["OnFilterToggle"] = function () {
      toggleFilters()
    }
    // @ts-ignore
    window.Grids.OnClick = function (grid, row, col) {
      // Disable header click for open column
      if (col === "open" && row.Kind === "Header") {
        return true
      }
      if (col === "open") setNavigateToTaskId(row.id)
      if (col === "ZoomFit" && grid) {
        const ganttZoomRange = getGanttVisibleRange({
          startDate: zoomFitStartDate,
          endDate: zoomFitEndDate,
        })
        grid.ZoomTo(ganttZoomRange.start, ganttZoomRange.end)
        return true
      }
      if (col === "linksAndAttachments") {
        const event = window.event
        const target = event?.target as HTMLElement
        const fileLink = target?.closest("[data-file-id]")
        if (fileLink) {
          const fileId = fileLink.getAttribute("data-file-id")
          if (fileId) {
            openFileInBrowser(fileId)
            return true
          }
        }
      }
    }

    window.Grids.OnGanttPage = function (grid, col, size) {
      if (window.Grids[id]) {
        const [left] = size as unknown as number[]
        // @ts-ignore
        const leftDate = window.Grids[id].GetGanttDate(left)
        scrollToDateRef.current = new Date(leftDate)
      }
    }

    window.Grids.OnGetExportValue = function (grid, row, col, value, format) {
      const task = row as any
      if (col === "statusDescription") {
        const tipValue = (row as any).statusDescriptionTip?.replaceAll("&nbsp;", " ").replaceAll("&bull;", "")
        const tipFormatted = tipValue ? `(${tipValue})` : ""
        const statusDescription = task.statusDescription?.toString().replaceAll("\n", " ") || ""
        const statusDescriptionExportValue = `${statusDescription} ${tipFormatted}`
        return statusDescriptionExportValue
      }
    }

    window.Grids.OnGetEnum = function (grid, row, col, enumValue) {
      if (col === "participants") {
        // @ts-ignore
        const taskManagers = row.managers?.split(";").filter(Boolean) || []
        const membershipOptions = getMembershipOptions(options)
        const participantOptions = membershipOptions.reduce((options, current) => {
          if (!taskManagers.includes(current.id)) {
            options.push(current.name)
          }
          return options
        }, [] as string[])
        return makeEnumString(participantOptions)
      }
      return enumValue
    }

    window.Grids.OnGetEnumKeys = function (grid, row, col, enumValue) {
      if (col === "participants") {
        // @ts-ignore
        const taskManagers = row.managers?.split(";").filter(Boolean) || []
        const membershipOptions = getMembershipOptions(options)
        const participantOptions = membershipOptions.reduce((options, current) => {
          if (!taskManagers.includes(current.id)) {
            options.push(current.id)
          }
          return options
        }, [] as string[])
        return makeEnumString(participantOptions)
      }
      return enumValue
    }

    window.Grids.OnGetGanttSideHtml = function (grid, row, col, width, complete, barType, plan, index, text, side) {
      // for right side
      if (side === 2 && plan === 0) {
        // @ts-expect-error - ts doesn't know row type
        return row.ganttGanttHtmlRight
      }
    }

    window.Grids.OnGanttMenu = function (grid, row, col, menu, xy) {
      // @ts-expect-error - ts doesn't know row type
      const isRowWithNoPlan = !row.plannedStartDate && !row.plannedEndDate
      if (isRowWithNoPlan) {
        menu.Items = menu.Items?.filter((item) => item.Name !== translations.deleteMainBar)
      }
    }
  }

  React.useEffect(() => {
    // Create a new treegrid instance at the beginning
    // or if one of showActualBar, showBaselineBar, locale, showFilters or showGantt changes
    createTreegrid({ id, layout, data: rows })
    return () => destroyTreegrid(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, showActualBar, showBaselineBar, showGantt, root.enableTimeComponent, project?.customProjectNumber])

  React.useEffect(() => {
    if (navigateToTaskId) {
      const path = createPathWithContext(`/tasks/${navigateToTaskId}/basic`, { withSubContext: true })
      history.push(path)
    }
    return () => {
      setNavigateToTaskId(null)
    }
  }, [createPathWithContext, history, navigateToTaskId])

  React.useEffect(() => {
    const grid = window.Grids[id]
    const isGridNotRendering = grid && grid?.Rendering === false
    if (showGantt && isGridNotRendering) {
      const ganttBaseDate = !root.enableTimeComponent
        ? rootNode.ganttBaseDate
          ? dateTimeService.startOfDay(new Date(rootNode.ganttBaseDate)).getTime()
          : undefined
        : rootNode.ganttBaseDate
      const timeZonedGanttBaseDate = ganttBaseDate
        ? dateTimeService.removeTimezoneOffset(new Date(ganttBaseDate), "UTC").getTime()
        : undefined

      const ganttFinishDate = !rootNode.enableTimeComponent
        ? rootNode.ganttFinishDate
          ? dateTimeService.endOfDay(new Date(rootNode.ganttFinishDate)).getTime()
          : undefined
        : rootNode.ganttFinishDate
      const timeZonedGanttFinishDate = ganttFinishDate
        ? dateTimeService.removeTimezoneOffset(new Date(ganttFinishDate), "UTC").getTime()
        : undefined

      grid.SetGanttBase(timeZonedGanttBaseDate)
      grid.SetGanttFinish(timeZonedGanttFinishDate)
    }
  }, [id, dateFormat, showGantt, rootNode, root.enableTimeComponent, dateTimeService])

  return {
    id,
    rootNode,
    translations,
    showGuide,
    handleGuideComplete,
  }
}

const TaskTreeGrid = ({ isFullScreen = true, ...taskTreeGridProps }: TaskTreeGridProps) => {
  const { id, showGuide, handleGuideComplete } = useTaskTreeGrid(taskTreeGridProps)
  const translations = useTranslation("treegrid").t
  return (
    <div id={id} style={{ height: "calc(100vh - 124px)" }} data-test="task-list-container">
      {showGuide && (
        <OnboardingGuideModal
          gridId={id}
          message={[
            translations("taskTreeGridGuideAddTaskMessage"),
            translations("taskTreeGridGuideDragAndDropMessage"),
            translations("taskTreeGridGuideScheduleMessage"),
          ]}
          onComplete={handleGuideComplete}
          allowMask
        />
      )}
    </div>
  )
}
export default TaskTreeGrid

function adaptValue({ column, newValue, oldValue }: { column: string; newValue: any; oldValue: any }) {
  let adaptedValue: any = newValue
  if (isDateColumn(column)) {
    if (newValue) adaptedValue = new Date(newValue).toISOString()
    else adaptedValue = ""
  }
  if (isSelectionColumn(column)) {
    const oldArray: string[] = oldValue.split(";").filter(Boolean)
    const newArray: string[] = newValue.split(";").filter(Boolean)
    const add = newArray.filter((id) => !oldArray.includes(id))
    const remove = oldArray.filter((id) => !newArray.includes(id))
    adaptedValue = { add, remove }
  }
  if (column === "state") {
    adaptedValue = getStateFromStateIcon(newValue)
  }
  if (column === "ganttBarColor") {
    adaptedValue = getColorFromColorHtml(newValue)
  }
  return adaptedValue
}

const isDateColumn = (columnName: string) => {
  return (
    columnName === "actualEndDate" ||
    columnName === "actualStartDate" ||
    columnName === "plannedEndDate" ||
    columnName === "plannedStartDate"
  )
}

const isSelectionColumn = (columnName: string) => {
  return (
    columnName === "participants" ||
    columnName === "managers" ||
    columnName === "suppliers" ||
    columnName === "workspaces"
  )
}

const getEnrichedTasks = (updatedTasks: TaskViewModel[], tasks: TaskViewModel[]) => {
  // Enrich updated tasks with links and files from original data
  // The API response may not include these, so we preserve them from the source
  return updatedTasks.map((updatedTask) => {
    const originalTask = tasks.find((t) => t.id === updatedTask.id)
    if (originalTask) {
      return {
        ...updatedTask,
        links: originalTask.links || [],
        files: originalTask.files || [],
        canViewTaskAttachments: originalTask.canViewTaskAttachments,
      }
    }
    return updatedTask
  })
}

const updateRowsInGrid = ({
  grid,
  dateFormat,
  timeZone,
  updatedTasks,
  translations,
  tasks,
}: UpdateRowsInGridFnArgs) => {
  if (!grid || !updatedTasks) return
  // PRESERVE LINKS AND FILES FROM EXISTING ROW
  const enrichedTasks = getEnrichedTasks(updatedTasks, tasks)
  const taskRows = enrichedTasks.map((task: TaskViewModel) =>
    makeTreeGridTask({ task, dateFormat, timeZone, translations })
  )
  syncDataFromServerToGrid(grid, taskRows)
}

type UpdateRowsInGridFnArgs = {
  grid: TGrid
  dateFormat: string
  timeZone: TimeZoneType
  updatedTasks: TaskViewModel[]
  translations: TreeGridTranslations
  tasks: TaskViewModel[]
}

type ProjectTaskListProps = {
  project: ProjectViewModel
  task?: undefined
}
type SubTaskListProps = {
  task: TaskViewModel
  project?: undefined
}
type TaskTreeGridProps = (ProjectTaskListProps | SubTaskListProps) & {
  tasks: TaskViewModel[]
  calendarSettings?: CalendarSettingsViewModel // TODO: REMOVE OPTIONAL WHEN EVERYTHING IS HOOKED
  onAddTask: (
    treegridNewTaskData: Omit<taskAPI.NewBulkTaskData, "projectId" | "orgId">
  ) => Promise<taskAPI.BulkCreateTasksReturnData | void>
  showGantt?: boolean
  showActualBar?: boolean
  showBaselineBar?: boolean
  isFullScreen?: boolean
}
